/* Phase 6A-3b — F3. CEO Dashboard tile: BOM Override Hotspots
 *
 * Reads GET /api/designer/analytics/bom-overrides and surfaces:
 *  - override rate %
 *  - top 5 swapped-out items (what engineers are removing/changing)
 *  - top 5 swapped-in items (what they install instead)
 *  - top reasons recorded
 *
 * Visible only to CEO + ADMIN (the endpoint enforces this; the tile silently
 * unmounts on 403 so other roles see nothing).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wrench, ArrowRight, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";

function PctRing({ pct, tone = "amber" }) {
  const tones = {
    amber: "stroke-amber-400",
    red: "stroke-red-400",
    emerald: "stroke-emerald-400",
    sky: "stroke-sky-400",
  };
  const value = Math.max(0, Math.min(100, Number(pct) || 0));
  const r = 28;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 80 80" className="h-20 w-20" data-testid="ring-svg">
      <circle cx="40" cy="40" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
      <motion.circle
        cx="40"
        cy="40"
        r={r}
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
        transform="rotate(-90 40 40)"
        className={tones[tone] || tones.amber}
        initial={{ strokeDasharray: `0 ${c}` }}
        animate={{ strokeDasharray: `${(value / 100) * c} ${c}` }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      />
      <text
        x="40" y="44"
        textAnchor="middle"
        className="fill-current font-heading"
        fontSize="14"
      >
        {value.toFixed(0)}%
      </text>
    </svg>
  );
}

function MiniBar({ count, max, tone = "sky" }) {
  const pct = max ? (count / max) * 100 : 0;
  const tones = {
    sky: "bg-sky-400",
    amber: "bg-amber-400",
    emerald: "bg-emerald-400",
    red: "bg-red-400",
  };
  return (
    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${Math.max(4, pct)}%` }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`h-full ${tones[tone] || tones.sky} rounded-full`}
      />
    </div>
  );
}

export default function BomOverrideTile() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/designer/analytics/bom-overrides")
      .then(({ data }) => setData(data))
      .catch((e) => {
        if (e?.response?.status === 403) setHidden(true);
      })
      .finally(() => setLoading(false));
  }, []);

  if (hidden) return null;

  if (loading) {
    return (
      <div
        className="glass p-5 animate-pulse h-44"
        data-testid="bom-override-tile-loading"
      />
    );
  }
  if (!data) return null;

  const s = data.summary || {};
  const swappedOut = data.top_swapped_out || [];
  const swappedIn = data.top_swapped_in || [];
  const reasons = data.by_reason || [];
  const maxOut = Math.max(1, ...swappedOut.map((r) => r.count));
  const maxIn = Math.max(1, ...swappedIn.map((r) => r.count));

  const rateTone =
    s.override_rate_pct >= 40 ? "red" : s.override_rate_pct >= 20 ? "amber" : "emerald";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="glass p-5 relative overflow-hidden"
      data-testid="bom-override-tile"
      data-tour-id="bom-override-tile"
    >
      <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-gradient-primary opacity-10 blur-2xl" />

      <div className="flex items-start justify-between mb-3 relative">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Wrench className="h-5 w-5 text-amber-300" />
          </div>
          <div>
            <div className="text-[10px] tracking-widest text-amber-300 font-heading">
              BOM OVERRIDE HOTSPOTS
            </div>
            <div className="font-heading text-base tracking-wide">
              What engineers swap in the field
            </div>
            <div className="text-[11px] text-foreground/55 mt-0.5">
              Tracked: {s.tracked || 0} designs · Untracked: {s.untracked || 0}{" "}
              {s.untracked > 0 && (
                <span className="text-foreground/40" title="Untracked = designs saved before Phase 6A-3b shipped, no original_bom snapshot">
                  (pre-6A-3b)
                </span>
              )}
            </div>
          </div>
        </div>
        <Link to="/sales/designer/designs" data-testid="bom-override-tile-link-wrap">
          <Badge
            data-testid="bom-override-tile-link"
            className="bg-sky-500/10 text-sky-300 border-sky-500/30 cursor-pointer hover:bg-sky-500/20"
          >
            All designs <ArrowRight className="h-3 w-3 ml-1" />
          </Badge>
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[160px_1fr_1fr] gap-4 mt-3">
        {/* Override-rate ring */}
        <div className="flex flex-col items-center justify-center" data-testid="bom-override-rate-ring">
          <PctRing pct={s.override_rate_pct || 0} tone={rateTone} />
          <div className="text-[10px] uppercase tracking-widest text-foreground/55 font-heading mt-2">
            Override Rate
          </div>
          <div className="text-[11px] text-foreground/60 mt-0.5">
            {s.designs_with_overrides || 0} / {s.tracked || 0} designs
          </div>
        </div>

        {/* Top swapped-out */}
        <div data-testid="bom-override-swapped-out">
          <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest text-red-300 font-heading">
            <TrendingDown className="h-3 w-3" /> Top swapped OUT
          </div>
          {swappedOut.length === 0 ? (
            <div className="text-xs text-foreground/40">No removals yet.</div>
          ) : (
            <div className="space-y-2">
              {swappedOut.map((r, idx) => (
                <div key={idx} className="text-xs" data-testid={`swap-out-row-${idx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-foreground/85 truncate pr-2">{r.item}</span>
                    <span className="font-mono text-red-300 shrink-0">×{r.count}</span>
                  </div>
                  <MiniBar count={r.count} max={maxOut} tone="red" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top swapped-in */}
        <div data-testid="bom-override-swapped-in">
          <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest text-emerald-300 font-heading">
            <TrendingUp className="h-3 w-3" /> Top swapped IN
          </div>
          {swappedIn.length === 0 ? (
            <div className="text-xs text-foreground/40">No additions yet.</div>
          ) : (
            <div className="space-y-2">
              {swappedIn.map((r, idx) => (
                <div key={idx} className="text-xs" data-testid={`swap-in-row-${idx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-foreground/85 truncate pr-2">{r.item}</span>
                    <span className="font-mono text-emerald-300 shrink-0">×{r.count}</span>
                  </div>
                  <MiniBar count={r.count} max={maxIn} tone="emerald" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {reasons.length > 0 && (
        <div
          className="mt-4 pt-3 border-t border-white/5 flex flex-wrap items-center gap-2"
          data-testid="bom-override-reasons"
        >
          <div className="text-[10px] uppercase tracking-widest text-foreground/55 font-heading mr-1 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 text-amber-400" /> Reasons:
          </div>
          {reasons.slice(0, 6).map((r, i) => (
            <Badge
              key={i}
              data-testid={`reason-chip-${i}`}
              variant="outline"
              className="text-[10px] border-amber-500/30 text-amber-200/90"
            >
              {r.reason} · {r.count}
            </Badge>
          ))}
        </div>
      )}
    </motion.div>
  );
}
